import {GetAPIBaseURLByService, GetAPIHost} from "./API";
import {mPrint} from "../mPrint";
import {GrayLog} from "../../grayLog/grayLog";
import {AESKey, AESTool, MD5Tool} from "../encrypt";
import {RoutePath} from "../../router/routePath";
import {differ} from "../util";
import {AppConfig} from "../../appConfig";

const ResponseCode = {
    ok: 0,
}

const APIMap = new Map<string, any>();
const RequestControllers = new Map<string, AbortController>();

const getAPIHash = (api: string, param: string) => {
    return MD5Tool.encrypt(api + param + (new Date).getTime())
}

interface RequestError extends Error {
    statusCode?: number
}

const onGetHttpError = async (response: Response) => {
    const errMsg = await response.text()
    const httpErr: RequestError = new Error()
    httpErr.statusCode = response.status
    httpErr.name = response.statusText
    httpErr.message = errMsg
    throw httpErr;
}

const onGetNetworkError = (error: any) => {
    mPrint(error)
    const networkErr: RequestError = new Error()
    networkErr.statusCode = 0
    networkErr.name = "Internet disconnected"
    networkErr.message = "Network failure, please check"
    throw networkErr;
}

const timeoutPromise = (url: string, timeout: number): Promise<Response> => {
    return new Promise((resolve) => {
        differ(() => {
            resolve(new Response("Request timeout", {status: 504, statusText: "Request timeout"}))
            // const controller = RequestControllers.get(url)
            // controller && controller.abort()
        }, timeout)
    })
}

const requestPromise = (url: string, options: any) => {
    const controller = new AbortController()
    RequestControllers.set(url, controller)

    return fetch(url, Object.assign({}, {
        credentials: "include",
        headers: {
            "Content-Type": "application/json",
        },
        signal: controller.signal,
    }, options))
        .then((response) => {
            mPrint(`${url} => response\n`, response)
            RequestControllers.delete(url)
            return response
        })
        .catch((networkErr) => {
            mPrint(`${url} => networkErr\n`, networkErr)
            throw onGetNetworkError(networkErr)
        })
}

const request = (url: string, options: any) => {
    mPrint(`${url} => param\n`, options.body);

    return Promise.race([timeoutPromise(url, AppConfig.requestTimeout), requestPromise(url, options)])
}

const requestJson = async (url: string, options: any) => {
    const response = await request(url, options)
    if (response.ok) {
        return response.json()
    }

    throw await onGetHttpError(response)
}

const requestBlob = async (url: string, options: any) => {
    const response = await request(url, options)
    if (response.ok) {
        return response.blob()
    }

    throw await onGetHttpError(response)
}

const postUrl = (url: string, param = {}) => requestJson(url, {
    body: JSON.stringify(param),
    method: "POST",
})

const assembleGetApi = (api: string, param: any): string => {
    let _api = api;
    if (Object.keys(param).length > 0) {
        let paramsString = Object.keys(param)
            .map(k => encodeURIComponent(k) + "=" + encodeURIComponent(param[k]))
            .join("&");
        _api = api + "?" + paramsString
    }
    return _api
}

const getUrl = (url: string) => {
    return requestJson(url, {
        method: "GET",
    })
}

const downloadFile = (url: string, param: object, method: HttpMethod) => {
    if (method === HttpMethod.post) {
        return requestBlob(url, {
            body: JSON.stringify(param),
            method: "POST",
        })
    }
    return requestBlob(url, {
        method: "GET",
    })
}

const onGraylog = (apiHash: string, url: string, param: object, statusCode: number, errMsg?: string) => {
    let obj = APIMap.get(apiHash);
    let additionalInfo;
    if (obj && obj.beginDate) {
        let endDate = new Date();
        additionalInfo = {duration: (endDate.getTime() - obj.beginDate.getTime())};
    }

    APIMap.delete(apiHash);

    const api = url.replace(GetAPIHost(), "")
    const paramStr = JSON.stringify(param)

    GrayLog.network(
        api,
        statusCode,
        Object.assign({
            param: (statusCode !== 200 && paramStr) ? AESTool.encrypt(paramStr, AESKey.log) : "",
            errMsg: errMsg || "",
        }, additionalInfo || {})
    )
}

export interface APIError {
    code: number,
    msg: string,
}

export type SuccessCallback = (response: any) => void
export type FailCallback = (error: APIError) => void

const onFinish = (apiHash: string, url: string, param: object, response: any, success: SuccessCallback, fail?: FailCallback) => {
    mPrint(`${url} => result\n`, response)

    if (response.code === ResponseCode.ok) {
        success && success(response)
    } else {
        fail && fail({code: response.code, msg: response.message})
    }

    onGraylog(apiHash, url, param, 200)
}

const onError = (apiHash: string, url: string, param: object, error: any, fail?: FailCallback) => {
    mPrint(`${url} => error ${error?.statusCode}\n`, error)

    const statusCode = error?.statusCode || 0
    const errMsg = error?.message || (statusCode === 401 ? "Login timeout" : "Request failed")

    if (statusCode === 401 && window.location.href.includes(RoutePath.loginFailed) === false){
        alert("Session timed out, please log in again.")
        window.location.replace(`${window.location.origin}/#${RoutePath.loginFailed}`)
    }

    fail && fail({code: statusCode, msg: errMsg})

    onGraylog(apiHash, url, param, statusCode, errMsg)
}

enum HttpMethod {
    get,
    post,
}

// todo: send batch requests

export const sendRequestUrl = (url: string, param: object, method: HttpMethod, success: SuccessCallback, fail?: FailCallback): string => {
    const apiHash = getAPIHash(url, JSON.stringify(param));
    APIMap.set(apiHash, {beginDate: (new Date())});

    (method === HttpMethod.get ? getUrl(url) : postUrl(url, param))
        .then((response) => {
            onFinish(apiHash, url, param, response, success, fail)
        })
        .catch((error) => {
            onError(apiHash, url, param, error, fail)
        })

    return apiHash
}

export const sendRequest = (api: string, param: object, success: SuccessCallback, fail?: FailCallback): string => {
    const url = `${GetAPIBaseURLByService()}${api}`;
    return sendRequestUrl(url, param, HttpMethod.post, success, fail)
}

export const sendGetRequest = (api: string, param: object, success: SuccessCallback, fail?: FailCallback): string => {
    const url = `${GetAPIBaseURLByService()}${assembleGetApi(api, param)}`;
    return sendRequestUrl(url, param, HttpMethod.get, success, fail)
}

const onDownloadSuccess = (data: Blob, fileName: string) => {
    let a = document.createElement("a");
    a.href = window.URL.createObjectURL(data);
    a.target = "_blank"
    a.download = fileName
    a.click();
}

const onDownloadFail = (api: string, err: any) => {
    mPrint(`${api} => error ${err?.statusCode}\n`, err)
    alert("Coming Soon")
}

/**
 * @Desc   download file with HTTP GET
 * @Params
 * @Return
*/
export const getFile = (api: string, fileName: string, param: object) => {
    const url = `${GetAPIBaseURLByService()}${assembleGetApi(api, param)}`;
    downloadFile(url, param, HttpMethod.get)
        .then((data) => {
            onDownloadSuccess(data, fileName)
        })
        .catch((err) => {
            onDownloadFail(api, err)
        })
}

/**
 * @Desc   download file with HTTP POST
 * @Params
 * @Return
*/
export const fetchFile = (api: string, fileName: string, param: object) => {
    const url = `${GetAPIBaseURLByService()}${api}`;
    downloadFile(url, param, HttpMethod.post)
        .then((data) => {
            onDownloadSuccess(data, fileName)
        })
        .catch((err) => {
            onDownloadFail(api, err)
        })
}
